import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, CheckCircle, Circle, Edit2, AlertCircle, Palette, Calendar } from 'lucide-react';
import { useTasks } from '../lib/context/TaskContext';
import { useTheme } from '../lib/theme';
import type { Task } from '../types/database';

export const TaskSidebar = () => {
  const { tasks, loading, addTask, toggleTask, deleteTask, editTask } = useTasks();
  const { accentColor } = useTheme();
  const [newTask, setNewTask] = useState('');
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const dragTaskRef = useRef<Task | null>(null);

  const colors = [
    { name: 'Indigo', value: '#4f46e5' },
    { name: 'Rose', value: '#e11d48' },
    { name: 'Green', value: '#10b981' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#8b5cf6' },
    { name: 'Amber', value: '#f59e0b' }
  ];

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTask.trim()) return;

    try {
      setError(null);
      await addTask(newTask.trim(), '#4f46e5'); // Default to indigo color
      setNewTask('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add task');
    }
  };

  const handleColorChange = async (taskId: string, color: string) => {
    try {
      setError(null);
      await editTask(taskId, undefined, color);
      setShowColorPicker(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task color');
    }
  };

  const handleEditTask = async (taskId: string) => {
    try {
      setError(null);
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setEditingTask(taskId);
        setEditText(task.title);
      }
    } catch (err) {
      console.error('Error starting edit:', err);
      setError(err instanceof Error ? err.message : 'Failed to start editing task');
    }
  };

  const handleSaveEdit = async (taskId: string) => {
    try {
      setError(null);
      if (!editText.trim()) {
        throw new Error('Task title cannot be empty');
      }
      await editTask(taskId, editText.trim());
      setEditingTask(null);
      setEditText('');
    } catch (err) {
      console.error('Error saving edit:', err);
      setError(err instanceof Error ? err.message : 'Failed to save task edit');
    }
  };

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    dragTaskRef.current = task;
    e.dataTransfer.setData('application/json', JSON.stringify(task));
    e.dataTransfer.effectAllowed = 'move';
    
    // Get the original element
    const element = e.currentTarget as HTMLElement;
    
    // Create a clone of the element for dragging
    const clone = element.cloneNode(true) as HTMLElement;
    clone.id = 'dragging-task-clone';
    clone.style.position = 'fixed';
    clone.style.top = '-1000px';
    clone.style.left = '-1000px';
    clone.style.width = `${element.offsetWidth}px`;
    clone.style.height = `${element.offsetHeight}px`;
    clone.style.opacity = '0.8';
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '9999';
    clone.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.1)';
    clone.style.transform = 'rotate(2deg)';
    
    // Add the clone to the document
    document.body.appendChild(clone);
    
    // Set the drag image to the clone
    e.dataTransfer.setDragImage(clone, 20, 20);
    
    // Add a class to the original element
    element.classList.add('task-dragging');
    
    // Update the clone position during drag
    const updateClonePosition = (moveEvent: MouseEvent) => {
      if (clone) {
        clone.style.left = `${moveEvent.clientX - 20}px`;
        clone.style.top = `${moveEvent.clientY - 20}px`;
      }
    };
    
    // Add and remove event listeners
    document.addEventListener('mousemove', updateClonePosition);
    
    // Clean up on drag end
    const cleanUp = () => {
      document.removeEventListener('mousemove', updateClonePosition);
      document.removeEventListener('dragend', cleanUp);
      if (clone && clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
      element.classList.remove('task-dragging');
    };
    
    document.addEventListener('dragend', cleanUp);
  };

  return (
    <div className="w-80 bg-white dark:bg-dark-800 border-l border-gray-200 dark:border-dark-700 h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 dark:border-dark-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tasks</h2>
        
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400" />
            <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          </div>
        )}

        <form onSubmit={handleAddTask} className="relative flex items-center">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Add a new task..."
            className={`w-full pl-4 pr-12 py-2.5 bg-indigo-50/50 dark:bg-indigo-900/10 border-0 rounded-lg text-gray-900 dark:text-white placeholder-indigo-400 dark:placeholder-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 text-sm`}
          />
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            type="submit"
            className="absolute right-2 p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
          </motion.button>
        </form>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            No tasks yet. Add one above!
          </div>
        ) : (
          <AnimatePresence initial={false}>
            <div className="space-y-2">
              {tasks.map(task => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-gray-50 dark:bg-dark-700 rounded-lg group cursor-move"
                  draggable
                  onDragStart={(e) => handleDragStart(e, task)}
                >
                  {editingTask === task.id ? (
                    <div className="p-3 flex items-center space-x-2">
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="flex-1 px-3 py-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEdit(task.id);
                          } else if (e.key === 'Escape') {
                            setEditingTask(null);
                            setEditText('');
                          }
                        }}
                        autoFocus
                      />
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleSaveEdit(task.id)}
                        className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => {
                          setEditingTask(null);
                          setEditText('');
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </motion.button>
                    </div>
                  ) : (
                    <div className="p-3 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => toggleTask(task.id, !task.completed)}
                          className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                        >
                          {task.completed ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <Circle className="h-5 w-5" />
                          )}
                        </motion.button>
                        <span className={`text-sm ${
                          task.completed
                            ? 'text-gray-400 dark:text-gray-500 line-through'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {task.title}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setShowColorPicker(task.id)}
                          className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                        >
                          <Palette className="h-4 w-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEditTask(task.id)}
                          className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                        >
                          <Edit2 className="h-4 w-4" />
                        </motion.button>
                        {showColorPicker === task.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="absolute right-0 mt-2 p-2 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 z-10"
                          >
                            <div className="grid grid-cols-3 gap-1">
                              {colors.map(color => (
                                <button
                                  key={color.value}
                                  onClick={() => handleColorChange(task.id, color.value)}
                                  className="w-6 h-6 rounded-full"
                                  style={{ backgroundColor: color.value }}
                                  title={color.name}
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => deleteTask(task.id)}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <X className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};